import React, { useState } from 'react';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Input from '../components/Common/Input';
import Button from '../components/Common/Button';
import styles from './SearchTrainsPage.module.css';

const SearchTrainsPage: React.FC = () => {
  const navigate = useNavigate();
  const [fromStation, setFromStation] = useState('');
  const [toStation, setToStation] = useState('');
  const [errors, setErrors] = useState<{ from?: string; to?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { from?: string; to?: string } = {};

    if (!fromStation.trim()) {
      newErrors.from = 'Please enter origin station code';
    }

    if (!toStation.trim()) {
      newErrors.to = 'Please enter destination station code';
    }

    if (fromStation.trim() === toStation.trim()) {
      newErrors.to = 'Origin and destination cannot be same';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      navigate(`/search-trains/${fromStation.toUpperCase()}/${toStation.toUpperCase()}`);
      setIsLoading(false);
    }
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 10);
    setFromStation(value);
    if (errors.from) {
      setErrors({ ...errors, from: undefined });
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 10);
    setToStation(value);
    if (errors.to) {
      setErrors({ ...errors, to: undefined });
    }
  };

  return (
    <div className={styles.searchTrainsPage}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FiSearch className={styles.titleIcon} />
            Search Trains
          </h1>
          <p className={styles.subtitle}>
            Find all available trains between any two stations
          </p>
        </div>

        {/* Form */}
        <Card className={styles.formCard}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGrid}>
              <Input
                type="text"
                value={fromStation}
                onChange={handleFromChange}
                placeholder="Enter station code (e.g., NDLS)"
                label="From Station"
                error={errors.from}
                icon={<FiSearch />}
                fullWidth
                maxLength={10}
                autoFocus
              />

              <div className={styles.arrowIcon}>
                <FiArrowRight />
              </div>

              <Input
                type="text"
                value={toStation}
                onChange={handleToChange}
                placeholder="Enter station code (e.g., BCT)"
                label="To Station"
                error={errors.to}
                icon={<FiSearch />}
                fullWidth
                maxLength={10}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isLoading}
            >
              {isLoading ? 'Searching Trains...' : 'Search Trains'}
            </Button>

            <div className={styles.hint}>
              <p>💡 Enter station codes to find all direct trains between stations</p>
            </div>
          </form>
        </Card>

        {/* Examples */}
        <div className={styles.examplesSection}>
          <Card className={styles.exampleCard}>
            <h3 className={styles.exampleTitle}>Popular Routes</h3>
            <div className={styles.exampleRoutes}>
              <div
                className={styles.exampleRoute}
                onClick={() => {
                  setFromStation('NDLS');
                  setToStation('BCT');
                }}
              >
                NDLS → BCT
              </div>
              <div
                className={styles.exampleRoute}
                onClick={() => {
                  setFromStation('HWH');
                  setToStation('NDLS');
                }}
              >
                HWH → NDLS
              </div>
              <div
                className={styles.exampleRoute}
                onClick={() => {
                  setFromStation('MAS');
                  setToStation('NDLS');
                }}
              >
                MAS → NDLS
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SearchTrainsPage;